import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box } from "../../../components/Box";
import { Flex } from "../../../components/Flex";
import { Paragraph } from "../../../components/Paragraph";
import { H3 } from "../../../components/Heading";
import { Button } from "../../../components/Button";
import { Input } from "../../../components/Input";
import { Card } from "../../../components/Card";
import { Loader } from "../../../components/Loader";
import { ROUTE } from "../../../routes";
import { NewProfileFrame } from "../NewProfileFrame";
import { useGetProfileList } from "../../../data/useGetProfileList";
import theme from "../../../theme";
import { DatePicker } from "../../../components/DatePicker";
import { useCountries } from "../../../data/useCountries";
import { useEntityTypes } from "../../../data/useEntityTypes";
import { DateTime } from "luxon";
import { capitalise, stripHtmlTags } from "../../../utils/string";
import Select from "../../../components/Select";
import { useCompanyStatusOptions } from "../../../data/fallbackPayloads/useCompanyStatusOptions";
import { yourReferenceValidation } from "../../../utils/validationSchema";
import { useMembershipDivisionTeam } from "../../../hooks/useMembershipDivisionTeam";
import { useAuth } from "../../../context/AuthContext";

type Mode = "search" | "selectOrCreate" | "addNew";

type EntityFormState = {
  division: number | string;
  team: number | string;
  name: string;
  country: number | null;
  entityType: number | null;
  status: number | null;
  yourReference: string;
  registrationNumber: string;
  dateOfRegistration: string;
};

const emptyForm: EntityFormState = {
  division: "",
  team: "",
  name: "",
  country: null,
  entityType: null,
  status: null,
  yourReference: "",
  registrationNumber: "",
  dateOfRegistration: "",
};

export const NewEntityProfile = () => {
  const navigate = useNavigate();
  const location = useLocation() as {
    state?: { newEntity?: EntityFormState };
  };

  const restoredForm = location.state?.newEntity
    ? {
        ...location.state.newEntity,
        entityType:
          location.state.newEntity.entityType != null
            ? Number(location.state.newEntity.entityType)
            : null,
        country:
          (location.state.newEntity as any).country != null
            ? Number((location.state.newEntity as any).country)
            : null,
        status:
          (location.state.newEntity as any).status != null &&
          (location.state.newEntity as any).status !== ""
            ? Number((location.state.newEntity as any).status)
            : null,
      }
    : null;

  const [mode, setMode] = useState<Mode>(restoredForm ? "addNew" : "search");

  const [regDraft, setRegDraft] = useState<Date | null>(
    restoredForm?.dateOfRegistration
      ? DateTime.fromISO(restoredForm.dateOfRegistration).toJSDate()
      : null,
  );

  const [entityName, setEntityName] = useState("");
  const [entityNumber, setEntityNumber] = useState("");
  const [submittedName, setSubmittedName] = useState("");
  const [submittedNumber, setSubmittedNumber] = useState("");

  const [selectedEntityId, setSelectedEntityId] = useState<number | null>(null);

  const {
    divisionOptions,
    showDivisionField,
    singleDivisionId,
    teamOptionsFor,
    singleTeamId,
  } = useMembershipDivisionTeam();
  const { hasPermission } = useAuth();

  const [form, setForm] = useState<EntityFormState>(
    restoredForm ?? {
      ...emptyForm,
      division: singleDivisionId,
      team: singleTeamId,
    }
  );

  const [yourReferenceError, setYourReferenceError] = useState<string>();

  const { data: countries } = useCountries();
  const { data: entityTypes } = useEntityTypes();

  const entityTypeOptions =
    entityTypes?.data.map(({ id, name }) => ({
      label: name,
      value: id,
    })) ?? [];

  const countryOptions =
    countries?.data.map(({ id, name }) => ({
      label: name,
      value: id,
    })) ?? [];

  const { options: companyStatusOptions } = useCompanyStatusOptions();

  const { data, isLoading } = useGetProfileList({
    type: "entity",
    search: submittedName || undefined,
    registrationNumber: submittedNumber || undefined,
  });

  const results = data?.data?.results ?? [];

  const handleSearch = () => {
    setSubmittedName(entityName.trim());
    setSubmittedNumber(entityNumber.trim());
    setSelectedEntityId(null);
    setMode("selectOrCreate");
  };

  const handleChangeForm =
    (field: keyof EntityFormState) => (eOrValue: any) => {
      let value = "";

      if (typeof eOrValue === "string") {
        value = eOrValue;
      } else if (eOrValue && eOrValue.currentTarget) {
        value = eOrValue.currentTarget.value;
      } else if (eOrValue && eOrValue.target) {
        value = eOrValue.target.value;
      }

      value = stripHtmlTags(value);

      if (field === "yourReference") {
        try {
          yourReferenceValidation.validateSync(value);
          setYourReferenceError(undefined);
        } catch (err: any) {
          setYourReferenceError(err.message);
        }
      }

      setForm((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  const handleNextWithExisting = () => {
    if (!selectedEntityId) return;
    navigate(`/profile/${selectedEntityId}`);
  };

  const handleNextWithNewEntity = () => {
    if (!form.name.trim()) return;

    navigate(ROUTE.PROFILE_NEW_ENTITY_ADDRESS, {
      state: {
        newEntity: {
          division: form.division,
          team: form.team,
          name: form.name.trim(),
          country: form.country,
          entityType: form.entityType,
          status: form.status,
          yourReference: form.yourReference.trim(),
          registrationNumber: form.registrationNumber.trim(),
          dateOfRegistration: form.dateOfRegistration.trim(),
        },
      },
    });
  };

  const renderSearch = () => (
    <Box mb={3} minWidth={600}>
      <H3>Search Entity</H3>
      <Paragraph fontSize={1} mb={3}>
        Please enter entity details to search for its profile.
      </Paragraph>

      <Paragraph mb={1}>Entity Name</Paragraph>
      <Input
        placeholder="Enter entity name"
        value={entityName}
        onChange={(e) => setEntityName(stripHtmlTags(e.currentTarget.value))}
        mb={3}
      />

      <Paragraph mb={1}>Entity Number</Paragraph>
      <Input
        placeholder="Enter entity number"
        value={entityNumber}
        onChange={(e) => setEntityNumber(stripHtmlTags(e.currentTarget.value))}
        mb={3}
      />

      <Flex justifyContent="flex-end" mt={2}>
        <Button onClick={handleSearch}>Search</Button>
      </Flex>
    </Box>
  );

  const renderSelectOrCreate = () => (
    <>
      {isLoading ? (
        <Flex justifyContent="center" alignItems="center" minHeight="25vh">
          <Loader />
        </Flex>
      ) : (
        <Box mb={3} width="650px" maxWidth="calc(100vw - 64px)">
          <H3>Select Entity or Add New</H3>
          <Paragraph fontSize={1} mb={3}>
            Please select an entity from the list.
          </Paragraph>

          <Card
            mb={2}
            p={3}
            style={{ cursor: "pointer" }}
            onClick={() => setMode("addNew")}
          >
            <Paragraph fontWeight={500}>Create new entity</Paragraph>
          </Card>

          {results.length === 0 && (
            <Paragraph mt={3} color="gray.40">
              No matching entities found. You can create a new entity above.
            </Paragraph>
          )}

          {results.map((entity: any) => {
            const isSelected = selectedEntityId === entity.id;

            const fullAddress = entity.address
              ? [
                  entity.address[0]?.flat_appartment_subbuilding,
                  entity.address[0]?.building_number,
                  entity.address[0]?.building_name,
                  entity.address[0]?.road_street,
                  entity.address[0]?.town_city,
                  entity.address[0]?.district,
                  entity.address[0]?.post_zip_code,
                ]
                  .filter(Boolean)
                  .join(", ")
              : null;
            const country =
              countries?.data.find((c: any) => c?.id === entity?.country?.id) ?? null;
            const countryLabel = country?.name;
            const countryFlag = country?.icon;

            const entityType =
              entity?.entity_type && entityTypes?.data
                ? entityTypes.data.find((t: any) => t.id === entity.entity_type?.id)
                : null;
            const entityTypeName = entityType?.name ?? "-";

            const incorporationDate = entity.date_of_incorporation
              ? DateTime.fromISO(entity.date_of_incorporation).toFormat(
                  "dd/MM/yyyy",
                )
              : null;

            const statusLabel =
              typeof entity.status === "string"
                ? entity.status
                : entity.status?.label ?? entity.status?.status ?? "";

            return (
              <Card
                key={entity.id}
                mb={2}
                p={3}
                style={{
                  cursor: "pointer",
                  border: isSelected
                    ? `1px solid ${theme.colors.blue.primary}`
                    : "1px solid transparent",
                  boxShadow: isSelected
                    ? "0 0 0 1px rgba(73, 133, 255, 0.2)"
                    : "none",
                }}
                onClick={() => setSelectedEntityId(entity.id)}
              >
                <Flex justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Paragraph fontWeight={600} mb={1}>
                      {entity.name || "-"}
                      {statusLabel && (
                        <span style={{ marginLeft: 8, fontWeight: 400 }}>
                          {statusLabel}
                        </span>
                      )}
                    </Paragraph>

                    <Paragraph fontSize={1} color="gray.60">
                      {fullAddress || "-"}
                    </Paragraph>

                    {countryLabel && countryFlag ? (
                      <Flex gap="6" alignItems="center" mt={1}>
                        <img
                          src={`data:image/png;base64,${countryFlag}`}
                          alt="flag"
                        />
                        <Paragraph fontSize={1} color="gray.60" my={0}>
                          {capitalise(countryLabel)}
                        </Paragraph>
                      </Flex>
                    ) : (
                      <Paragraph fontSize={1} color="gray.60" mt={1}>
                        -
                      </Paragraph>
                    )}

                    <Flex justifyContent="space-between" mt={2} gap="12">
                      <Paragraph fontSize={1} color="gray.60" my={0}>
                        {entityTypeName}
                        {incorporationDate && ` • ${incorporationDate}`}
                      </Paragraph>
                      <Paragraph fontSize={1} color="gray.60" my={0}>
                      {entity.registration_number || "-"}
                      </Paragraph>
                    </Flex>
                  </Box>
                </Flex>
              </Card>
            );
          })}

          <Flex mt={3} justifyContent="space-between">
            <Button variant="text" onClick={() => setMode("search")}>
              Back
            </Button>
            <Button
              onClick={handleNextWithExisting}
              isDisabled={!selectedEntityId}
            >
              Next
            </Button>
          </Flex>
        </Box>
      )}
    </>
  );

  const renderAddNewEntity = () => {
    const relevantTeamOptions = teamOptionsFor(form.division);
    const isNextDisabled =
      !form.name.trim() ||
      !form.entityType ||
      Boolean(yourReferenceError) ||
      (showDivisionField && !form.division) ||
      (relevantTeamOptions.length > 0 && !form.team);

    return (
      <Box mb={3} minWidth={600}>
        <H3>New Entity</H3>

        <Input
          label="Entity name"
          isRequired
          placeholder="Entity name"
          value={form.name}
          onChange={handleChangeForm("name")}
          mb={3}
        />

        {showDivisionField && (
          <Select
            value={divisionOptions?.find((o) => o.value === form.division)}
            options={divisionOptions}
            onChange={(option: any) => {
              const newDivisionTeams = teamOptionsFor(option.value);
              setForm((prev) => ({
                ...prev,
                division: option.value,
                team:
                  newDivisionTeams.length === 1
                    ? newDivisionTeams[0].value
                    : "",
              }));
            }}
            minWidth={200}
            flex={1}
            label="Division"
            name="division"
            isRequired
            isDisabled={!hasPermission("profile.add_division")}
            title={
              hasPermission("profile.add_division")
                ? undefined
                : "You don't have permission to choose a division"
            }
            mb={3}
          />
        )}
        {relevantTeamOptions.length > 0 && (
          <Select
            key={form.division}
            value={relevantTeamOptions.find((o) => o.value === form.team)}
            options={relevantTeamOptions}
            onChange={(option: any) =>
              setForm((prev) => ({ ...prev, team: option.value }))
            }
            minWidth={200}
            flex={1}
            label="Team"
            name="team"
            isRequired
            isDisabled={!hasPermission("profile.add_team")}
            title={
              hasPermission("profile.add_team")
                ? undefined
                : "You don't have permission to choose a team"
            }
            mb={3}
          />
        )}

        <Input
          label="Your reference"
          placeholder="Your Reference"
          value={form.yourReference}
          onChange={handleChangeForm("yourReference")}
          maxLength={50}
          hasError={Boolean(yourReferenceError)}
          errorMessage={yourReferenceError}
          mb={3}
        />

        <Select
          value={countryOptions.find((o) => o.value === form.country)}
          options={countryOptions}
          onChange={(option: any) =>
            setForm((prev) => ({
              ...prev,
              country: option.value as number,
            }))
          }
          minWidth={200}
          flex={1}
          label="Country of registration/establishment"
          name="country"
          mb={3}
        />

        <Flex gap="16" mb={3}>
          <Box flex={1}>
            <Select
              value={entityTypeOptions.find((o) => o.value === form.entityType)}
              options={entityTypeOptions}
              onChange={(option: any) =>
                setForm((prev) => ({
                  ...prev,
                  entityType: option.value as number,
                }))
              }
              minWidth={200}
              flex={1}
              label="Entity Type"
              name="entity_type"
              isRequired
            />
          </Box>

          <Box flex={1}>
            <Select
              value={companyStatusOptions.find((o) => o.value === form.status)}
              options={companyStatusOptions}
              onChange={(option: any) =>
                setForm((prev) => ({
                  ...prev,
                  status: option?.value ?? null,
                }))
              }
              minWidth={200}
              flex={1}
              label="Current status"
              name="status"
            />
          </Box>
        </Flex>

        <Flex gap="16">
          <Box flex={1}>
            <Input
              placeholder="Registration number"
              label="Registration number"
              value={form.registrationNumber}
              onChange={handleChangeForm("registrationNumber")}
            />
          </Box>
          <Box flex={1}>
            <DatePicker
              value={regDraft}
              onChange={(d: Date | string | null) => {
                if (d instanceof Date && !Number.isNaN(d.getTime())) {
                  setRegDraft(d);
                  setForm((prev) => ({
                    ...prev,
                    dateOfRegistration:
                      DateTime.fromJSDate(d).toISODate() ?? "",
                  }));
                }

                if (d === null) {
                  setRegDraft(DateTime.now().toJSDate());
                  setForm((prev) => ({ ...prev, dateOfRegistration: "" }));
                }
              }}
              maxDate={DateTime.now().toJSDate()}
              label="Date of registration/establishment"
              name="dateOfRegistration"
            />
          </Box>
        </Flex>

        <Flex mt={3} justifyContent="space-between">
          <Button variant="text" onClick={() => setMode("selectOrCreate")}>
            Back
          </Button>
          <Button onClick={handleNextWithNewEntity} isDisabled={isNextDisabled}>
            Next
          </Button>
        </Flex>
      </Box>
    );
  };

  return (
    <NewProfileFrame mode="entity" step="entityDetails">
      {mode === "search" && renderSearch()}
      {mode === "selectOrCreate" && renderSelectOrCreate()}
      {mode === "addNew" && renderAddNewEntity()}
    </NewProfileFrame>
  );
};
